'use client';

import { useRef, useState } from 'react';
import { useAudio } from '@/app/contexts/AudioContext';

// Height of the audio row the mobile HUD card grows by while the audio UI is
// open (AudioContext.isVisible — the byline's "Listen" button). MobileHUD adds
// this to the card height and shifts its expand hotspots up by the same amount.
export const MOBILE_HUD_AUDIO_BAR_HEIGHT_PX = 44;

// Compact narration controls for the mobile bottom HUD strip: play/pause, a
// drag/tap scrub bar, an mm:ss readout, and the speed select. The desktop
// HUD's narration bar is GraphAudioControls/AudioPlayer; this is its
// touch-sized sibling, mounted by MobileHUD as a row under the strip graph.
export function MobileHudAudioBar({
  textColor,
  backgroundColor,
}: {
  textColor: string;
  backgroundColor: string;
}) {
  const {
    isPlaying,
    duration,
    currentTime,
    isLoading,
    togglePlayPause,
    setCurrentTime,
    formatTime,
    playbackRate,
    setPlaybackRate,
  } = useAudio();

  const progressBarRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Seek to the position under the pointer, clamped to the bar (same logic as
  // AudioPlayer's desktop scrub).
  const seekToClientX = (clientX: number) => {
    if (!progressBarRef.current || duration === 0) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setCurrentTime(percentage * duration);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (duration === 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
    seekToClientX(e.clientX);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    seekToClientX(e.clientX);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    setIsDragging(false);
  };

  const progressFraction = duration > 0 ? currentTime / duration : 0;

  return (
    <div
      className="relative flex h-full w-full items-center gap-2 pl-1 pr-3 font-mono"
      style={{ color: textColor }}
    >
      {/* Hairline rule separating the row from the graph's dither above. */}
      <div
        aria-hidden
        className="absolute inset-x-3 top-0 h-px"
        style={{ backgroundColor: textColor, opacity: 0.2 }}
      />
      <button
        type="button"
        onClick={togglePlayPause}
        className="flex h-10 w-9 shrink-0 items-center justify-center"
        aria-label={isPlaying ? 'Pause audio' : 'Play audio of page text'}
      >
        {isPlaying ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <rect x="5" y="3" width="5" height="18" rx="1"></rect>
            <rect x="14" y="3" width="5" height="18" rx="1"></rect>
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="12" viewBox="0 0 9 16" fill="currentColor" aria-hidden>
            <path d="M0 11.9523V4.04773C0 3.22232 0.911256 2.72209 1.60763 3.16523L7.81833 7.1175C8.46426 7.52854 8.46426 8.47146 7.81833 8.8825L1.60763 12.8348C0.911255 13.2779 0 12.7777 0 11.9523Z"></path>
          </svg>
        )}
      </button>
      {/* Scrub bar: the padded wrapper is the touch target (touch-none so the
          drag owns the gesture instead of scrolling the page); pointer capture
          keeps a drag alive when the finger strays off the thin track. */}
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        className="relative min-w-0 flex-1 cursor-pointer touch-none py-[14px]"
      >
        <div ref={progressBarRef} className="relative h-[3px] w-full">
          <div
            className="absolute inset-0 rounded-full"
            style={{ backgroundColor: textColor, opacity: 0.25 }}
          />
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-accent"
            style={{ width: `${progressFraction * 100}%` }}
          />
          {/* Playhead dot, knocked out against the track with a page-color ring. */}
          <div
            className="absolute top-1/2 h-[11px] w-[11px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent"
            style={{
              left: `${progressFraction * 100}%`,
              border: `2px solid ${backgroundColor}`,
            }}
          />
        </div>
      </div>
      <div className="shrink-0 whitespace-nowrap text-[10px] tabular-nums">
        {isLoading ? '0:00 / --:--' : `${formatTime(currentTime)} / ${formatTime(duration)}`}
      </div>
      <select
        value={playbackRate}
        onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
        aria-label="Playback speed"
        className="shrink-0 cursor-pointer appearance-none border-none bg-transparent text-[11px] font-bold focus:outline-none"
        style={{ width: playbackRate.toString().length * 7 + 10, color: textColor }}
      >
        <option value="0.5">0.5x</option>
        <option value="0.75">0.75x</option>
        <option value="1">1x</option>
        <option value="1.25">1.25x</option>
        <option value="1.5">1.5x</option>
        <option value="2">2x</option>
        <option value="2.5">2.5x</option>
        <option value="3">3x</option>
      </select>
    </div>
  );
}
